import React from 'react';

interface StatCardProps {
  title: string;
  value: number;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  color: string;
  iconColor: string;
  borderColor: string;
  change?: {
    value: string;
    isPositive: boolean;
  };
  description?: string;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  Icon,
  color,
  iconColor,
  borderColor,
  change,
  description
}) => {
  return (
    <div 
      className="group relative overflow-hidden rounded-2xl bg-white shadow-md transition-all duration-300 hover:-translate-y-1 hover:scale-[1.02] hover:shadow-lg"
      style={{ 
        borderTop: `3px solid ${borderColor}`,
      }}
    >
      {/* Content Container */}
      <div className="relative h-full p-3 sm:p-4">
        <div className="flex h-full flex-col items-start justify-between gap-3 sm:flex-row sm:items-start sm:gap-4">
          {/* Text Content */}
          <div className="flex min-w-0 flex-1 flex-col justify-center space-y-1 sm:space-y-2">
            {/* Title */}
            <div className="flex items-center">
              <p 
                className="break-words text-xs font-bold uppercase leading-tight tracking-wider"
                style={{ color: color }}
              >
                {title}
              </p>
            </div>
            
            {/* Value */}
            <p 
              className="text-xl font-bold leading-none tracking-tight sm:text-2xl"
              style={{ color: color }}
            >
              {value.toLocaleString()}
            </p>
            
            {/* Change Indicator */}
            {change && (
              <div className="flex items-center space-x-2">
                <div 
                  className={`rounded-lg px-2 py-1 text-xs font-medium ${
                    change.isPositive 
                      ? 'bg-green-100 text-green-600' 
                      : 'bg-red-100 text-red-600'
                  }`}
                >
                  <div className="flex items-center space-x-1">
                    <div 
                      className={`size-1.5 rounded-full ${
                        change.isPositive ? 'bg-green-500' : 'bg-red-500'
                      }`}
                    />
                    <span>{change.value}</span>
                  </div>
                </div>
              </div>
            )}
            
            {/* Description */}
            {description && (
              <p className="text-xs leading-relaxed text-gray-500">
                {description}
              </p>
            )}
          </div>
          
          {/* Icon Container */}
          <div 
            className="flex size-10 shrink-0 items-center justify-center self-end rounded-xl shadow-sm sm:mt-1 sm:size-12 sm:self-start"
            style={{ 
              backgroundColor: `${color}15`,
            }}
          >
            <Icon 
              className="size-5 sm:size-6" 
              style={{ color: iconColor }}
            />
          </div>
        </div>
      </div>
      
      {/* Subtle Hover Effect */}
      <div 
        className="absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-300 group-hover:opacity-5"
        style={{ 
          background: `radial-gradient(circle at center, ${color} 0%, transparent 70%)`,
        }}
      />
    </div>
  );
};

export default StatCard;